/* @license
 * Copyright 2025 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * Wraps a worker so that each postMessage returns a promise. The worker is
 * expected to post messages of the shape `{id, result, err}`.
 */
export const promisifyWorker = (worker) => {
    let nextId = 0;
    const pending = new Map();
    worker.onmessage = (event) => {
        var _a;
        const { id, result, err } = (_a = event.data) !== null && _a !== void 0 ? _a : {};
        const handlers = pending.get(id);
        if (handlers == null) {
            console.error(`Unknown message id "${id}" received from worker.`);
            return;
        }
        pending.delete(id);
        if (err != null) {
            handlers.reject(new Error(err));
        }
        else {
            handlers.resolve(result);
        }
    };
    worker.onerror = (event) => {
        event.preventDefault();
        pending.forEach(({ reject }, id) => {
            reject(new Error(`Worker error${event.message ? `: ${event.message}` : ''}`));
            pending.delete(id);
        });
    };
    return (payload, transfer) => {
        const id = nextId++;
        const promise = new Promise((resolve, reject) => {
            pending.set(id, { resolve, reject });
        });
        worker.postMessage({ id, payload }, transfer !== null && transfer !== void 0 ? transfer : []);
        return promise;
    };
};
//# sourceMappingURL=promisify-worker.js.map